/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import type { ParsedTimeSeriesCsv } from '@/lib/timeSeries/parseTimeSeriesCsv';

export const DEFAULT_TIME_SERIES_PREVIEW_LIMIT = 100;

export function getParsedTimeSeriesRowCount(parsedTimeSeries: ParsedTimeSeriesCsv): number {
    return parsedTimeSeries.plotXAxisValues.length;
}

export function getParsedTimeSeriesPreviewRows(
    parsedTimeSeries: ParsedTimeSeriesCsv,
    limit = DEFAULT_TIME_SERIES_PREVIEW_LIMIT,
): Record<string, string>[] {
    const rowCount = getParsedTimeSeriesRowCount(parsedTimeSeries);
    const previewRowCount = Math.min(rowCount, limit);

    return Array.from({ length: previewRowCount }, (_, rowIndex) => {
        const row: Record<string, string> = {};

        parsedTimeSeries.columns.forEach((column) => {
            if (column === parsedTimeSeries.plotXAxisColumn) {
                row[column] = parsedTimeSeries.plotXAxisValues[rowIndex] ?? '';
                return;
            }

            const value = parsedTimeSeries.plotYAxisValues[column]?.[rowIndex];
            row[column] = typeof value === 'number' && !Number.isNaN(value)
                ? String(value)
                : '';
        });

        return row;
    });
}

function parseTimeSeriesTimestamp(value: string): number | null {
    const timestamp = Date.parse(value.replace(' ', 'T'));

    return Number.isNaN(timestamp) ? null : timestamp;
}

export function getParsedTimeSeriesIntervalMs(
    parsedTimeSeries: ParsedTimeSeriesCsv,
): number | null {
    const timestamps = parsedTimeSeries.plotXAxisValues
        .slice(0, 5)
        .map(parseTimeSeriesTimestamp);

    if (timestamps.length < 5 || timestamps.some((timestamp) => timestamp === null)) {
        return null;
    }

    const intervals = timestamps.slice(1).map((timestamp, index) => (
        timestamp! - timestamps[index]!
    ));
    const [firstInterval] = intervals;

    if (!firstInterval || firstInterval <= 0 || intervals.some((interval) => interval !== firstInterval)) {
        return null;
    }

    return firstInterval;
}

export function formatTimeSeriesInterval(
    intervalMs: number | null,
    locale = 'de-DE',
): string {
    if (intervalMs === null) {
        return 'N/A';
    }

    const units = [
        { label: 'd', value: 24 * 60 * 60 * 1000 },
        { label: 'h', value: 60 * 60 * 1000 },
        { label: 'min', value: 60 * 1000 },
        { label: 's', value: 1000 },
    ];
    const formatter = new Intl.NumberFormat(locale, {
        maximumFractionDigits: 3,
    });
    const parts: string[] = [];
    let remainingInterval = intervalMs;

    for (const unit of units) {
        const amount = Math.floor(remainingInterval / unit.value);

        if (amount > 0) {
            parts.push(`${formatter.format(amount)} ${unit.label}`);
            remainingInterval -= amount * unit.value;
        }
    }

    if (parts.length > 0) {
        return parts.join(' ');
    }

    return `${formatter.format(intervalMs)} ms`;
}
